import { useMemo } from "react";
import { Link } from "react-router-dom";
import useSeo, { SITE_NAME, SITE_ORIGIN } from "@/hooks/useSeo";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SectionHeading from "@/components/feature/SectionHeading";
import { siteInfo, categories } from "@/mocks/categories";
import { milestones, testimonials, values } from "@/mocks/site";

export default function About() {
  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: `${SITE_NAME} · 关于我们`,
        url: `${SITE_ORIGIN}/about`,
        inLanguage: "zh-CN",
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
            { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_ORIGIN}/about` },
          ],
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_ORIGIN,
        description: siteInfo.intro,
        email: siteInfo.email,
      },
    ],
    []
  );

  useSeo({
    title: "关于我们 | 吃瓜前线的内容原则与定位",
    description:
      "吃瓜前线专注把网爆吃瓜现场直播、秒删截图与实时楼内容整理成清晰时间线。本页介绍网站定位、发展理念、内容原则与用户口碑。",
    keywords: "吃瓜前线,网爆吃瓜,网爆吃瓜实时楼,内容原则",
    path: "/about",
    jsonLd,
  });

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100 px-4 pb-10 pt-8 md:px-8 md:pb-14 md:pt-10">
        <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 font-label text-xs font-medium text-primary-800">
          <i className="ri-quill-pen-line" aria-hidden="true"></i>
          关于吃瓜前线
        </span>
        <h1 className="mt-4 max-w-3xl font-heading text-2xl font-black leading-tight tracking-tight text-foreground-950 md:text-4xl">
          我们不做站队，只把一件事讲完整
        </h1>
        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">{siteInfo.intro}</p>
      </div>

      <div className="px-4 py-12 md:px-8 md:py-16">
        <SectionHeading eyebrow="网站定位" title="一个可以安心浏览的热点档案馆" />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {values.map((item) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h3 className="font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-background-100 px-4 py-12 md:px-8 md:py-16">
        <SectionHeading eyebrow="发展理念" title="一路是怎么走过来的" desc="从一份内部文档，到六个长期更新的内容方向。" />
        <ol className="flex flex-col gap-4">
          {milestones.map((item) => (
            <li
              key={item.year}
              className="flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-5 md:flex-row md:items-center md:gap-8"
            >
              <span className="font-heading text-2xl font-bold text-primary-600 md:w-24">{item.year}</span>
              <div className="md:flex-1">
                <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="px-4 py-12 md:px-8 md:py-16">
        <SectionHeading eyebrow="内容原则" title="六个长期更新的方向" desc="每个方向都有自己的整理节奏与来源偏好。" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group flex cursor-pointer items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className={cat.icon} aria-hidden="true"></i>
              </span>
              <span>
                <span className="block font-heading text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
                  {cat.name}
                </span>
                <span className="mt-1 block text-sm leading-relaxed text-foreground-600">{cat.desc}</span>
              </span>
            </Link>
          ))}
        </div>
      </div>

      <div className="bg-background-100 px-4 py-12 md:px-8 md:py-16">
        <SectionHeading eyebrow="用户口碑" title="他们这样说起这里" />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {testimonials.map((item) => (
            <figure key={item.name} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6">
              <i className="ri-double-quotes-l text-2xl text-primary-400" aria-hidden="true"></i>
              <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.quote}</blockquote>
              <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 font-heading text-sm font-bold text-secondary-800">
                  {item.name.slice(0, 1)}
                </span>
                <span>
                  <span className="block font-label text-sm font-medium text-foreground-900">{item.name}</span>
                  <span className="block font-label text-xs text-foreground-500">{item.role}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      <div className="px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 items-center gap-6 rounded-lg border border-background-200 bg-background-50 p-6 md:grid-cols-2 md:p-10">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">想提供一条线索？</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              欢迎把公开可见的来源发给我们。我们会先核验来源类型，确认属于公开讨论范围后才会考虑整理。
            </p>
            <p className="mt-4 font-label text-sm text-foreground-700">联系邮箱：{siteInfo.email}</p>
          </div>
          <div className="flex flex-col gap-3 md:items-end">
            <Link
              to="/contact"
              className="w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-center font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:w-auto"
            >
              前往联系我们
            </Link>
            <Link
              to="/rank"
              className="w-full cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-center font-label text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 md:w-auto"
            >
              先看看热榜
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}